import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_RETRIES = 2;
const RECORDING_TIMEOUT = 120000; // 2 minutes

/**
 * Custom hook for speech-to-text using Web Speech API
 * Provides voice recording and real-time transcription functionality
 *
 * Creates a fresh SpeechRecognition instance per recording session to avoid
 * stale instances after network errors (Chrome sends audio to Google servers).
 */
function useSpeechRecognition(lang) {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(false);

  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const shouldBeRecordingRef = useRef(false);
  const internalTranscriptRef = useRef('');
  const retryCountRef = useRef(0);

  // Check browser compatibility on mount
  useEffect(() => {
    const supported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
    setIsSupported(supported);
  }, []);

  // Factory: creates a fresh, configured SpeechRecognition instance
  const createRecognitionInstance = useCallback(() => {
    const SpeechRecognitionAPI = window.webkitSpeechRecognition || window.SpeechRecognition;
    if (!SpeechRecognitionAPI) return null;

    const instance = new SpeechRecognitionAPI();
    instance.lang = lang || 'es-ES';
    instance.continuous = true;
    instance.interimResults = true;
    instance.maxAlternatives = 1;
    return instance;
  }, [lang]);

  // Destroy current instance (remove handlers + stop)
  const destroyInstance = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      } catch (err) {
        // Instance may already be stopped
      }
      recognitionRef.current = null;
    }
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    shouldBeRecordingRef.current = false;

    destroyInstance();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Transfer internal transcript to exposed transcript when stopping
    if (internalTranscriptRef.current) {
      setTranscript(internalTranscriptRef.current.trim());
    }

    setIsRecording(false);
  }, [destroyInstance]);

  // Attach event handlers to a recognition instance
  const setupEventHandlers = useCallback(
    (instance) => {
      instance.onresult = (event) => {
        let completeTranscript = '';

        for (let i = 0; i < event.results.length; i++) {
          const transcriptPart = event.results[i][0].transcript;
          completeTranscript += transcriptPart + ' ';
        }

        internalTranscriptRef.current = completeTranscript.trim();
      };

      instance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);

        if (event.error === 'network' && retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          const retryNumber = retryCountRef.current;
          console.warn(`STT network error, retry ${retryNumber}/${MAX_RETRIES}`);

          // Destroy the broken instance
          destroyInstance();

          // Retry with exponential backoff
          const delay = 500 * retryNumber;
          setTimeout(() => {
            if (!shouldBeRecordingRef.current) return;

            try {
              const freshInstance = createRecognitionInstance();
              if (!freshInstance) {
                setError('Speech recognition not available');
                cleanup();
                return;
              }
              recognitionRef.current = freshInstance;
              setupEventHandlers(freshInstance);
              freshInstance.start();
            } catch (err) {
              console.error('Error retrying recognition:', err);
              setError('Failed to restart recording');
              cleanup();
            }
          }, delay);
          return;
        }

        if (event.error === 'not-allowed') {
          setError('Microphone access denied');
        } else if (event.error === 'no-speech') {
          setError('No speech detected');
        } else if (event.error === 'network') {
          setError('Network error - speech recognition requires internet connection');
        } else {
          setError(`Recognition error: ${event.error}`);
        }

        cleanup();
      };

      instance.onend = () => {
        if (shouldBeRecordingRef.current) {
          try {
            // Create a fresh instance for restart to avoid stale state
            destroyInstance();
            const freshInstance = createRecognitionInstance();
            if (!freshInstance) {
              cleanup();
              return;
            }
            recognitionRef.current = freshInstance;
            setupEventHandlers(freshInstance);
            freshInstance.start();
          } catch (err) {
            console.error('Error restarting recognition:', err);
            cleanup();
          }
        } else {
          setIsRecording(false);
        }
      };
    },
    [cleanup, createRecognitionInstance, destroyInstance],
  );

  // Start recording
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError('Speech recognition not supported in this browser');
      return;
    }

    // Prevent multiple starts
    if (shouldBeRecordingRef.current) {
      return;
    }

    try {
      setError(null);
      shouldBeRecordingRef.current = true;
      internalTranscriptRef.current = '';
      retryCountRef.current = 0;

      // Create a fresh instance for each recording session
      destroyInstance();
      const instance = createRecognitionInstance();
      if (!instance) {
        setError('Speech recognition not available');
        shouldBeRecordingRef.current = false;
        return;
      }
      recognitionRef.current = instance;

      setupEventHandlers(instance);

      instance.start();
      setIsRecording(true);

      // Set timeout to auto-stop after 2 minutes
      timeoutRef.current = setTimeout(() => {
        stopRecording();
        setError('Recording stopped automatically (2 minute limit)');
      }, RECORDING_TIMEOUT);
    } catch (err) {
      console.error('Error starting recognition:', err);
      setError('Failed to start recording');
      cleanup();
    }
  }, [isSupported, cleanup, createRecognitionInstance, destroyInstance, setupEventHandlers]);

  // Stop recording
  const stopRecording = useCallback(() => {
    cleanup();
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isRecording,
    transcript,
    error,
    isSupported,
    startRecording,
    stopRecording,
  };
}

export default useSpeechRecognition;
